import React, { useState, useEffect, useRef, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import LegalFooter from '../components/LegalFooter';
import '../styles/coastal-dusk.css';

// ============================================================================
// EMAIL LOGIN FORM
// ============================================================================
const EmailLoginForm = memo(({ onSubmit, onToggleMode, onForgotPassword, isLogin, loading, error, paymentSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(email.trim().toLowerCase(), password, false);
  };
  
  return (
    <div className="landing-form-card">
      <p className="landing-form-title">
        Already have an account?
      </p>
      
      {paymentSuccess && (
        <div className="message-success text-center">
          Payment successful! Please log in to start your session.
        </div>
      )}
      
      {error && (
        <div className="message-error text-center">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="max-w-sm mx-auto space-y-5">
        <div>
          <label className="landing-form-label">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.toLowerCase())}
            placeholder="your@email.com"
            required
            className="landing-form-input"
            data-testid="email-input"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            inputMode="email"
          />
        </div>
        <div>
          <label className="landing-form-label">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            required
            className="landing-form-input"
            data-testid="password-input"
          />
        </div>
        <div className="pt-5">
          <button
            type="submit"
            disabled={loading}
            className="landing-form-submit"
            data-testid="login-btn"
          >
            {loading ? 'Processing...' : isLogin ? 'Log In' : 'Register'}
          </button>
        </div>
      </form>
      
      <div className="flex justify-center gap-8 mt-8">
        <button onClick={onToggleMode} className="landing-ghost-link">
          {isLogin ? 'Register' : 'Have an account? Log In'}
        </button>
        <button onClick={onForgotPassword} className="landing-ghost-link">
          Forgot password?
        </button>
      </div>
    </div>
  );
});

// ============================================================================
// PASSCODE LOGIN FORM
// ============================================================================
const PasscodeLoginForm = memo(({ onSubmit, loading, error }) => {
  const [passcode, setPasscode] = useState('');
  const [password, setPassword] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');
    
    if (isNewUser) {
      if (password !== confirmPassword) {
        setLocalError('Passwords do not match');
        return;
      }
      if (password.length < 6) {
        setLocalError('Password must be at least 6 characters');
        return;
      }
    }
    
    onSubmit(passcode.trim().toUpperCase(), password, true, isNewUser);
  };
  
  return (
    <div className="landing-form-card">
      <p className="landing-form-title">
        Have a guest passcode?
      </p>
      
      {(error || localError) && (
        <div className="message-error text-center">
          {localError || error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="max-w-sm mx-auto space-y-5">
        <div>
          <label className="landing-form-label">Passcode</label>
          <input
            type="text"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value.toUpperCase())}
            placeholder="CJ-XXXXXX"
            required
            className="landing-form-input text-center"
            style={{ letterSpacing: '0.18em' }}
            data-testid="passcode-input"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck="false"
          />
        </div>
        <div>
          <label className="landing-form-label">{isNewUser ? 'Create Password' : 'Password'}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isNewUser ? "Create your password" : "Enter password"}
            required
            className="landing-form-input"
            data-testid="passcode-password-input"
          />
        </div>
        {isNewUser && (
          <div>
            <label className="landing-form-label">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              className="landing-form-input"
              data-testid="passcode-confirm-password-input"
            />
          </div>
        )}
        <div className="pt-5">
          <button
            type="submit"
            disabled={loading}
            className="landing-form-submit"
            data-testid="passcode-login-btn"
          >
            {loading ? 'Processing...' : isNewUser ? 'Create Password & Start' : 'Log In'}
          </button>
        </div>
      </form>
      
      <div className="flex justify-center mt-8">
        <button
          onClick={() => {
            setIsNewUser(!isNewUser);
            setLocalError('');
            setConfirmPassword('');
          }}
          className="landing-ghost-link"
        >
          {isNewUser ? 'Already created a password? Log In' : 'Create Password to Begin'}
        </button>
      </div>
    </div>
  );
});

// ============================================================================
// VIDEO PREVIEW COMPONENT
// ============================================================================
const VideoPreview = ({ videoId, alt, onClick }) => (
  <div 
    className="landing-video-preview mx-auto"
    onClick={onClick}
    data-testid={`video-preview-${videoId}`}
  >
    <img 
      src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
      alt={alt}
      className="w-full h-full object-cover"
      onError={(e) => { e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }}
    />
    <div className="play-overlay">
      <div className="play-circle">
        <svg viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z"/>
        </svg>
      </div>
    </div>
  </div>
);

// ============================================================================
// MAIN LANDING PAGE
// ============================================================================
const LandingPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMusicPrompt, setShowMusicPrompt] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showVideoLightbox, setShowVideoLightbox] = useState(false);
  const [lightboxVideoId, setLightboxVideoId] = useState(null);
  const [schoolsExpanded, setSchoolsExpanded] = useState(false);
  
  const { login, register, API, setToken: setAuthToken, setUser: setAuthUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const musicRef = useRef(null);

  const contactEmail = 'info@clarityjournalbyenquips.com';
  const instagramUrl = 'https://www.instagram.com/byenquips/';
  const linkedInUrl = 'https://www.linkedin.com/company/enquips/';

  // Music Controls
  const toggleMusic = () => {
    if (musicRef.current) {
      if (musicRef.current.paused) {
        musicRef.current.play().then(() => {
          setIsMusicPlaying(true);
          setShowMusicPrompt(false);
        }).catch(e => console.log('Could not play music:', e));
      } else {
        musicRef.current.pause();
        setIsMusicPlaying(false);
      }
    }
  };

  // Video Lightbox
  const openVideoLightbox = (videoId) => {
    if (musicRef.current && !musicRef.current.paused) {
      musicRef.current.pause();
      setIsMusicPlaying(false);
    }
    setLightboxVideoId(videoId);
    setShowVideoLightbox(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoLightbox = () => {
    setShowVideoLightbox(false);
    setLightboxVideoId(null);
    document.body.style.overflow = 'auto';
  };

  // Effects
  useEffect(() => {
    const originalTitle = document.title;
    document.title = 'Clarity Soundtrack';
    
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = 'AI-Powered Personal Audio / Wellness & Self-Reflection Tool. Clarity Soundtrack gives you your own insights narrated back to you. Answer 8 gentle prompts. Your thoughts get distilled and narrated back to you. Feel them land. Just you, heard.';
    document.head.appendChild(metaDescription);
    
    // Track traffic
    const utm_source = searchParams.get('utm_source');
    const utm_medium = searchParams.get('utm_medium');
    const utm_campaign = searchParams.get('utm_campaign');
    
    const trackTraffic = async () => {
      try {
        let trackingSession = sessionStorage.getItem('tracking_session');
        if (!trackingSession) {
          trackingSession = Date.now().toString();
          sessionStorage.setItem('tracking_session', trackingSession);
        }
        
        await axios.post(`${API}/analytics/track-traffic`, {
          utm_source: utm_source || 'direct',
          utm_medium: utm_medium,
          utm_campaign: utm_campaign,
          page: 'main',
          session_id: trackingSession
        });
      } catch (err) {
        console.log('Traffic tracking error (non-critical):', err);
      }
    };
    trackTraffic();
    
    // Check payment success
    const paymentSuccessParam = searchParams.get('payment_success');
    const sessionId = searchParams.get('session_id');
    
    if (paymentSuccessParam === 'true') {
      setPaymentSuccess(true);
      setIsLogin(true);
      
      if (sessionId) {
        const verifyPayment = async () => {
          try {
            await axios.get(`${API}/payment/status/${sessionId}`);
          } catch (err) {
            console.error('Error verifying payment:', err);
          }
        };
        verifyPayment();
      }
    }
    
    // Auto-play music
    const playMusic = async () => {
      if (musicRef.current) {
        try {
          musicRef.current.volume = 0.7;
          await musicRef.current.play();
          setIsMusicPlaying(true);
        } catch (err) {
          setShowMusicPrompt(true);
        }
      }
    };
    playMusic();

    return () => {
      document.title = originalTitle;
      try {
        document.head.removeChild(metaDescription);
      } catch (e) {}
    };
  }, []);

  // Form Handlers
  const handleSubmit = async (emailOrPasscode, password, isPasscode, isNewPasscodeUser = false) => {
    setError('');
    setPasscodeError('');
    setLoading(true);

    try {
      if (isPasscode) {
        try {
          if (isNewPasscodeUser) {
            const verifyResponse = await axios.post(`${API}/auth/passcode/verify`, {
              passcode: emailOrPasscode.toUpperCase()
            });
            
            if (!verifyResponse.data.valid) {
              setPasscodeError('Invalid or already used passcode');
              return;
            }
            
            const registerResponse = await axios.post(`${API}/auth/passcode/register`, {
              passcode: emailOrPasscode.toUpperCase(),
              password: password
            });
            
            const token = registerResponse.data.access_token || registerResponse.data.token;
            if (token) {
              localStorage.setItem('token', token);
              localStorage.setItem('user', JSON.stringify(registerResponse.data.user));
              setAuthToken(token);
              setAuthUser(registerResponse.data.user);
              await checkUserStatus();
            }
          } else {
            const response = await axios.post(`${API}/auth/passcode/login`, {
              passcode: emailOrPasscode.toUpperCase(),
              password: password
            });
            
            const token = response.data.access_token || response.data.token;
            if (token) {
              localStorage.setItem('token', token);
              localStorage.setItem('user', JSON.stringify(response.data.user));
              setAuthToken(token);
              setAuthUser(response.data.user);
              await checkUserStatus();
            }
          }
        } catch (err) {
          setPasscodeError(err.response?.data?.detail || (isNewPasscodeUser ? 'Failed to register with passcode' : 'Invalid passcode or password'));
        }
      } else if (isLogin) {
        const result = await login(emailOrPasscode, password);
        if (result.success) {
          const sessionId = searchParams.get('session_id');
          
          if (sessionId) {
            try {
              const token = localStorage.getItem('token');
              await axios.get(
                `${API}/payment/status/${sessionId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (err) {
              console.error('Error verifying payment:', err);
            }
          }
          
          await checkUserStatus();
        } else {
          setError(result.error);
        }
      } else {
        const result = await register(emailOrPasscode, password);
        if (result.success) {
          setError('');
          setIsLogin(true);
          alert('Registration successful! Please login.');
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const checkUserStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/interpretation/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const status = response.data;

      if (status.has_unused_access) {
        if (status.has_consented) {
          navigate('/settling-in');
        } else {
          navigate('/welcome');
        }
      } else if (status.has_generated_interpretation) {
        navigate('/interpretation');
      } else if (status.has_journal_progress && status.has_paid_access) {
        navigate('/journaling');
      } else if (status.has_consented && status.has_paid_access) {
        navigate('/settling-in');
      } else if (status.has_paid_access && !status.has_consented) {
        navigate('/welcome');
      } else if (status.has_lifetime_access) {
        navigate('/interpretation');
      } else {
        // No paid access — go to payment page
        navigate('/payment');
      }
    } catch (err) {
      console.error('Error checking user status:', err);
      navigate('/welcome');
    }
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'transparent' }}>
      {/* Blurred Background Image */}
      <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />
      
      {/* Gradient Orbs — Defocused light / foggy sun */}
      <div className="landing-orb warm focus-shift" style={{ '--orb-opacity': '0.75', top: '-8%', left: '-10%', width: '55vw', height: '55vw', maxWidth: '700px', maxHeight: '700px', animationDelay: '0s' }} />
      <div className="landing-orb cool" style={{ '--orb-opacity': '0.6', top: '12%', right: '-12%', width: '50vw', height: '50vw', maxWidth: '600px', maxHeight: '600px', animationDelay: '-6s' }} />
      <div className="landing-orb neutral focus-shift" style={{ '--orb-opacity': '0.5', top: '55%', left: '-8%', width: '55vw', height: '55vw', maxWidth: '650px', maxHeight: '650px', animationDelay: '-12s' }} />
      <div className="landing-orb warm" style={{ '--orb-opacity': '0.4', top: '80%', right: '-5%', width: '45vw', height: '45vw', maxWidth: '550px', maxHeight: '550px', animationDelay: '-4s' }} />
      <div className="landing-orb cool" style={{ '--orb-opacity': '0.35', top: '130%', left: '10%', width: '48vw', height: '48vw', maxWidth: '560px', maxHeight: '560px', animationDelay: '-9s' }} />
      <div className="landing-orb neutral" style={{ '--orb-opacity': '0.3', top: '170%', right: '5%', width: '42vw', height: '42vw', maxWidth: '500px', maxHeight: '500px', animationDelay: '-15s' }} />

      {/* Background Music */}
      <audio ref={musicRef} src="/assets/landing-music.mp3" loop autoPlay />

      {/* Music Toggle — Top Right */}
      <button 
        className="music-toggle-coastal"
        onClick={toggleMusic}
        data-testid="music-toggle"
        style={{ background: 'rgba(26, 26, 26, 0.45)', border: '1px solid rgba(163, 163, 163, 0.08)' }}
      >
        <span style={{ fontWeight: 300 }}>{isMusicPlaying ? 'Playing' : 'Music'}</span>
      </button>

      {/* Music Prompt */}
      {showMusicPrompt && (
        <div 
          className="music-prompt"
          onClick={async () => {
            if (musicRef.current && musicRef.current.paused) {
              try {
                musicRef.current.volume = 0.7;
                await musicRef.current.play();
                setIsMusicPlaying(true);
                setShowMusicPrompt(false);
              } catch (e) {
                console.error('Failed to start music:', e);
              }
            }
          }}
          style={{ 
            background: 'rgba(26, 26, 26, 0.6)', border: '1px solid rgba(163, 163, 163, 0.08)',
            whiteSpace: 'nowrap', padding: '12px 24px', fontSize: '0.82rem'
          }}
        >
          <span style={{ fontWeight: 300 }}>Tap to start background music</span>
        </div>
      )}

      {/* ========== HERO SECTION ========== */}
      <section className="min-h-[90vh] flex flex-col justify-end pb-20 sm:pb-28 relative px-6 sm:px-10">
        <div className="relative z-10 max-w-2xl">
          
          <h1 className="editorial-title headline-focus-in-d1 mb-3" style={{ textShadow: '0 0 40px rgba(184, 212, 208, 0.3), 0 0 80px rgba(184, 212, 208, 0.15)' }}>
            Clarity<br />Soundtrack
          </h1>
          
          <p className="headline-focus-in-d1" style={{ 
            fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.72rem', 
            letterSpacing: '0.18em', textTransform: 'uppercase', 
            color: 'rgba(184, 212, 208, 0.55)', marginBottom: '24px' 
          }}>
            by Annie Ratanab
          </p>
          
          <p className="editorial-subtitle headline-focus-in-d2 mb-10" style={{ maxWidth: '440px', lineHeight: 1.7 }}>
            Not entirely a meditation or journaling. 20 mins. Answer 8 gentle prompts. 
            Your thoughts distilled into a soundtrack you fall asleep to. 
            You hear them. Absorb them. Feel them land. Just you, heard.
          </p>
          
          <div className="headline-focus-in-d3 mb-4">
            <button
              onClick={() => navigate('/payment')}
              className="cta-pill-landing"
              data-testid="hero-cta"
            >
              Craft your soundtrack
            </button>
          </div>
          
          <div className="headline-focus-in-d4">
            <a 
              href="#login-section" 
              className="landing-ghost-link"
              onClick={(e) => { e.preventDefault(); document.getElementById('login-section')?.scrollIntoView({ behavior: 'smooth' }); }}
              style={{ 
                color: 'var(--accent-teal)', 
                opacity: 0.85, 
                textDecoration: 'underline', 
                textUnderlineOffset: '4px', 
                textDecorationColor: 'rgba(184, 212, 208, 0.4)',
                fontSize: '0.92rem'
              }}
            >
              or Listen to your soundtrack
            </a>
            <span style={{ 
              display: 'block', 
              fontFamily: 'var(--font-body)', 
              fontWeight: 300, 
              fontSize: '0.7rem', 
              letterSpacing: '0.12em', 
              textTransform: 'uppercase', 
              color: 'rgba(184, 212, 208, 0.4)', 
              marginTop: '6px' 
            }}>
              returning client
            </span>
          </div>
        </div>
      </section>

      {/* ========== EFFORTLESS & RESERVE ========== */}
      <section style={{ padding: '60px 0 40px' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 relative z-10">
          <p style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '10px' }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: 400 }}>Effortless</span>: Multiple example entries placed alongside each prompt to get you in the flow.
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: 400 }}>Reserve</span>: Your entries are never stored for your privacy.
          </p>
        </div>
      </section>

      {/* ========== STEP 01 — WHO IS THIS FOR ========== */}
      <section id="journey" style={{ padding: '80px 0' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 relative z-10">
          <h2 className="landing-step-heading">Who is this for</h2>
          
          {/* --- Single unified narrative --- */}
          <div>
            <p style={{ 
              fontFamily: 'var(--font-body)', 
              fontWeight: 400, 
              fontSize: '1.12rem', 
              color: 'var(--text-primary)', 
              lineHeight: 1.6, 
              marginBottom: '20px' 
            }}>
              You're looking for a way to work with the brain's natural process in 
              realigning what matters - bringing your internal value clarity (the 
              contentment from the inside) into alignment with your day-to-day 
              decisions.
            </p>
            <p className="landing-step-body" style={{ color: 'var(--text-secondary)' }}>
              Not optimization. Not fixing.
            </p>
            <p className="landing-step-body">
              Clarity Soundtrack is not entirely about relaxation. It has an integration 
              layer. It's a structured thinking environment - designed for the specific 
              cognitive state where your best long-term decisions get made.
            </p>
            <p className="landing-step-body">
              Most investors have an Investment Policy Statement (IPS) for their 
              portfolio. The outlines on how investments are managed. Think of this 
              as an IPS from somewhere inside. One that accounts for what you actually 
              want the portfolio to produce - in your life, alongside the numbers.
            </p>
          </div>
        </div>
      </section>

      <div className="landing-tide-line" />

      {/* ========== STEP 02 — ARTIFACTS ========== */}
      <section style={{ padding: '80px 0' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 relative z-10">
          <div className="landing-step-label">02</div>
          <h2 className="landing-step-heading">Artifacts</h2>
          
          {/* --- 01: Your Personalized Audio --- */}
          <h3 style={{ fontFamily: 'var(--font-body)', fontWeight: 400, fontSize: '1.05rem', color: 'var(--text-primary)', marginTop: '32px', marginBottom: '8px', letterSpacing: '0.02em' }}>
            <span style={{ fontSize: '0.7rem', letterSpacing: '0.14em', color: 'var(--accent-teal)', opacity: 0.5, marginRight: '10px' }}>01:</span>
            Your Personalized Audio
          </h3>
          <p className="landing-step-body" style={{ fontStyle: 'italic', color: 'var(--accent-teal)', opacity: 0.8, marginBottom: '24px' }}>
            Your words becomes a soundtrack you fall asleep to.
          </p>
          <p className="landing-step-body">
            Your Clarity Soundtrack takes the themes from your session and 
            narrates them back to you — in a format designed for absorption 
            without effort.
          </p>
          <p className="landing-step-body">
            This's not entirely about relaxation. It has an integration layer. The 
            audio delivers what surfaced during your session as structured input 
            — the kind your brain is already primed to process.
          </p>
          <p className="landing-step-body" style={{ marginTop: '36px' }}>
            As our brain doesn't simply rest at night, it consolidates, and 
            reorganizes. Your Clarity Soundtrack is designed to work with that 
            process.
          </p>
          <p className="landing-step-body">
            The inner work doesn't end when the session does. This is the next 
            step — the mechanism that takes what you reflected on and moves it 
            from insight to retained signal.
          </p>
          <p className="landing-step-body" style={{ fontSize: '1rem', color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: '28px' }}>
            Download the audio or return anytime for your personalized narration 
            gently looping.
          </p>
        </div>
      </section>

      {/* Audio Sample Video */}
      <section style={{ padding: '40px 0 20px' }}>
        <div className="max-w-xs sm:max-w-sm mx-auto px-6 relative z-10">
          <p className="text-center mb-5" style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.82rem', letterSpacing: '0.04em', color: 'rgba(184, 212, 208, 0.7)' }}>
            This is a sample. Yours will be built entirely from your own reflections.
          </p>
          <VideoPreview 
            videoId="rvyDm-F7RK8" 
            alt="Clarity Soundtrack - Audio Sample"
            onClick={() => openVideoLightbox('rvyDm-F7RK8')}
          />
        </div>
      </section>

      {/* Schools of Thought — Fold/Unfold */}
      <section style={{ padding: '40px 0 60px' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 relative z-10">
          <div className="text-center" style={{ marginBottom: schoolsExpanded ? '32px' : '0' }}>
            <button
              onClick={() => setSchoolsExpanded(!schoolsExpanded)}
              className="cta-pill-landing"
              style={{ fontSize: '0.75rem', padding: '14px 32px', letterSpacing: '0.04em' }}
              data-testid="schools-of-thought-toggle"
            >
              Schools of thoughts that inspire Clarity Soundtrack
              <span style={{ marginLeft: '10px', display: 'inline-block', transition: 'transform 0.3s', transform: schoolsExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
                &#9662;
              </span>
            </button>
          </div>

          <div style={{ 
            maxHeight: schoolsExpanded ? '2000px' : '0', 
            overflow: 'hidden', 
            transition: 'max-height 0.5s ease-in-out, opacity 0.4s ease',
            opacity: schoolsExpanded ? 1 : 0 
          }}>
            {/* Pillar 1: Structuring the Prompts */}
            <div style={{ marginBottom: '36px', marginTop: '8px' }}>
              <p style={{ 
                fontFamily: 'var(--font-body)', fontWeight: 400, fontSize: '0.95rem', 
                color: 'var(--text-primary)', letterSpacing: '0.02em', marginBottom: '12px' 
              }}>
                In Structuring the Prompts
              </p>
              <p className="landing-step-body">
                The 8-prompt arc is inspired by George Kinder's <span style={{ fontStyle: 'italic' }}>The 3 Domains of Freedom</span> and 
                EnQUIPs' own self-reflection — each note patched into a coherent arc.
              </p>
              <p className="landing-step-body" style={{ color: 'var(--accent-teal)', opacity: 0.7, fontSize: '0.88rem', letterSpacing: '0.04em' }}>
                Values &rarr; Freedom &rarr; Goals &rarr; Security &rarr; Legacy
              </p>
            </div>

            {/* Pillar 2: Gentle Language & Tone */}
            <div style={{ marginBottom: '36px' }}>
              <p style={{ 
                fontFamily: 'var(--font-body)', fontWeight: 400, fontSize: '0.95rem', 
                color: 'var(--text-primary)', letterSpacing: '0.02em', marginBottom: '12px' 
              }}>
                Gentle Language &amp; Tone
              </p>
              <p className="landing-step-body">
                Inspired by the teaching of Kristin Neff in <span style={{ fontStyle: 'italic' }}>Self-Compassion</span> in her book, 
                "Self-Compassion: The Proven Power of Being Kind to Yourself". Research 
                on specific, concrete imagery broadened the idea of spaciousness, and 
                infinite space has shown better mood effects guiding the therapeutic 
                role of mental imagery in treating stress and insomnia.
              </p>
            </div>

            {/* Pillar 3: The Sleep Soundtrack */}
            <div style={{ marginBottom: '36px' }}>
              <p style={{ 
                fontFamily: 'var(--font-body)', fontWeight: 400, fontSize: '0.95rem', 
                color: 'var(--text-primary)', letterSpacing: '0.02em', marginBottom: '12px' 
              }}>
                The Sleep Soundtrack
              </p>
              <p className="landing-step-body">
                Audio narration before or while drifting off sets specific, emotionally salient expectations. 
                Sleep then replays and consolidates aspects of that material — just as it does with 
                audiobooks and other pre-sleep experiences.
              </p>
              <p className="landing-step-body">
                Repeated exposure plus sleep-dependent consolidation makes those reframed 
                narratives more accessible the next day, similar to how imagery-based protocols 
                shift mood and symptom patterns over time.
              </p>
            </div>

            {/* Closing — What inspired us */}
            <div style={{ marginBottom: '16px' }}>
              <p className="landing-step-body" style={{ color: 'var(--text-secondary)' }}>
                The schools of thoughts above have inspired us to create Clarity Soundtrack:
              </p>
              <p className="landing-step-body">
                A gentle narrative to soften the day before sleep. Let something inside 
                feel heard. Increase tendency of the coherence between internal value 
                clarity (the contentment from the inside) and day-to-day decision making.
              </p>
            </div>

            {/* PDF Download */}
            <div className="text-center" style={{ marginTop: '40px' }}>
              <a
                href="/assets/schools-of-thought.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="landing-ghost-link"
                style={{ 
                  fontSize: '0.78rem', letterSpacing: '0.06em',
                  color: 'var(--accent-teal)', opacity: 0.6,
                  textDecoration: 'underline', textUnderlineOffset: '4px',
                  textDecorationColor: 'rgba(184, 212, 208, 0.3)'
                }}
                data-testid="schools-of-thought-pdf"
              >
                Download full references (PDF)
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="landing-tide-line" />

      {/* --- 02: The Clarity Map (PDF) --- */}
      <section style={{ padding: '80px 0' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 relative z-10">
          <h3 style={{ fontFamily: 'var(--font-body)', fontWeight: 400, fontSize: '1.05rem', color: 'var(--text-primary)', marginBottom: '8px', letterSpacing: '0.02em' }}>
            <span style={{ fontSize: '0.7rem', letterSpacing: '0.14em', color: 'var(--accent-teal)', opacity: 0.5, marginRight: '10px' }}>02:</span>
            The Clarity Map (PDF)
          </h3>
          <p className="landing-step-body" style={{ fontStyle: 'italic', color: 'var(--accent-teal)', opacity: 0.8, marginBottom: '24px' }}>
            Not everything you wrote. What it means.
          </p>
          <p className="landing-step-body">
            Your Clarity Map is a personal map of what emerged during your 8-prompt session.
          </p>
          <p className="landing-step-body">
            Not a recap. Not a summary. It won't repeat your journal entries back 
            to you. It distills them. What surfaced. Where your attention is being 
            called.
          </p>
        </div>
      </section>

      {/* Explore 8-themes Video */}
      <section style={{ padding: '40px 0 40px' }}>
        <div className="max-w-xs sm:max-w-sm mx-auto px-6 relative z-10">
          <VideoPreview 
            videoId="le2gD9wwo_A" 
            alt="Explore the 8 Themes"
            onClick={() => openVideoLightbox('le2gD9wwo_A')}
          />
          <p className="text-center mt-5" style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.82rem', letterSpacing: '0.08em', color: 'rgba(163, 163, 163, 0.55)' }}>
            Explore 8-themes
          </p>
        </div>
      </section>

      {/* Example PDF */}
      <section style={{ padding: '20px 0 60px' }}>
        <div className="max-w-xl mx-auto px-6 text-center relative z-10">
          <a
            href="/assets/advisor-landing/example_insights.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-pill-landing"
            style={{ fontSize: '0.75rem', padding: '12px 28px' }}
          >
            Example PDF
          </a>
        </div>
      </section>

      <div className="landing-tide-line" />

      {/* ========== STEP 03 — THE SESSION ========== */}
      <section style={{ padding: '80px 0' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 relative z-10">
          <div className="landing-step-label">03</div>
          <h2 className="landing-step-heading">The session</h2>
          <p className="landing-step-body" style={{ marginTop: '24px' }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: 400 }}>Effortless:</span>
          </p>
          <p className="landing-step-body">
            You'll explore 8 prompts. Multiple example entries placed alongside 
            each prompt to get you in the flow. Simply select the ones you resonate 
            with to start with.
          </p>
          <p className="landing-step-body" style={{ fontSize: '1rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Set aside 20-30 minutes of quiet time. Complete in one sitting, or return within 24 hours.
          </p>
        </div>
      </section>

      {/* Session Mood Video */}
      <section style={{ padding: '40px 0 60px' }}>
        <div className="max-w-xs sm:max-w-sm mx-auto px-6 relative z-10">
          <p className="text-center mb-5" style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.82rem', letterSpacing: '0.08em', color: 'rgba(184, 212, 208, 0.7)' }}>
            Session Mood
          </p>
          <VideoPreview 
            videoId="_PVBSuj3KWY" 
            alt="Session Mood"
            onClick={() => openVideoLightbox('_PVBSuj3KWY')}
          />
        </div>
      </section>

      <div className="landing-tide-line" />

      {/* ========== PRIVACY ========== */}
      <section style={{ padding: '80px 0' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 text-center relative z-10">
          <h2 className="landing-step-heading text-center" style={{ marginBottom: '28px' }}>Your privacy, protected</h2>
          <p className="landing-step-body text-center">
            Your journal entries are never stored. Once processed into insights, they are permanently deleted. 
            The insights we prepare are offered only to you — no human reviews your entries or your map.
          </p>
          <p className="landing-step-body text-center" style={{ color: 'rgba(163, 163, 163, 0.45)', fontSize: '0.88rem' }}>
            If you choose not to create a future login, your insights will be deleted when your 24-hour session ends.
          </p>
        </div>
      </section>

      <div className="landing-tide-line" />

      {/* ========== FINAL CTA ========== */}
      <section style={{ padding: '80px 0' }}>
        <div className="max-w-xl mx-auto px-6 text-center relative z-10">
          <p className="landing-step-body text-center" style={{ marginBottom: '32px', fontSize: 'clamp(1rem, 3vw, 1.15rem)', lineHeight: 1.8 }}>
            20 minutes. 8 prompts. Your thoughts distilled into a soundtrack you 
            fall asleep to. You hear them. Absorb them. Feel them land.
          </p>
          <button
            onClick={() => navigate('/payment')}
            className="cta-pill-landing"
            data-testid="start-session-btn"
          >
            Craft your soundtrack
          </button>
        </div>
      </section>

      <div className="landing-tide-line" />

      {/* ========== LOGIN SECTION ========== */}
      <section id="login-section" style={{ padding: '60px 0' }}>
        <div className="max-w-md mx-auto px-6 relative z-10">
          <EmailLoginForm 
            onSubmit={handleSubmit}
            onToggleMode={() => setIsLogin(!isLogin)}
            onForgotPassword={() => navigate('/forgot-password')}
            isLogin={isLogin}
            loading={loading}
            error={error}
            paymentSuccess={paymentSuccess}
          />
        </div>
      </section>

      {/* ========== PASSCODE SECTION ========== */}
      <section style={{ padding: '20px 0 80px' }}>
        <div className="max-w-md mx-auto px-6 relative z-10">
          <PasscodeLoginForm 
            onSubmit={handleSubmit}
            loading={loading}
            error={passcodeError}
          />
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="landing-footer">
        <p className="landing-footer-brand">The Clarity Soundtrack — by Annie Ratanab</p>
        <p style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.78rem', color: 'rgba(163, 163, 163, 0.4)', marginBottom: '16px', letterSpacing: '0.02em' }}>
          Not productivity. Not optimization. Just a quiet moment to reconnect with yourself.
        </p>
        <div className="landing-footer-links">
          <a href={`mailto:${contactEmail}`}>Email</a>
          <a href={linkedInUrl} target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a href={instagramUrl} target="_blank" rel="noopener noreferrer">Instagram</a>
        </div>
        <LegalFooter />
        <p className="landing-footer-copy">&copy; {new Date().getFullYear()} All rights reserved</p>
      </footer>

      {/* ========== VIDEO LIGHTBOX ========== */}
      {showVideoLightbox && lightboxVideoId && (
        <div 
          className="lightbox-overlay"
          onClick={closeVideoLightbox}
          data-testid="video-lightbox-overlay"
          style={{ zIndex: 9999 }}
        >
          {/* Close button - large and visible */}
          <button
            onClick={closeVideoLightbox}
            className="lightbox-close"
            data-testid="video-lightbox-close"
            style={{ 
              position: 'fixed', top: '16px', right: '16px', zIndex: 10001, 
              width: '48px', height: '48px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)', color: '#1a1a1a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none', cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
            }}
          >
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div 
            className="w-full h-full max-w-md mx-4 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full relative rounded-2xl overflow-hidden" style={{ aspectRatio: '9/16', maxHeight: 'calc(100vh - 120px)' }}>
              <iframe
                src={`https://www.youtube.com/embed/${lightboxVideoId}?autoplay=1&modestbranding=1&rel=0&showinfo=0&controls=1&iv_load_policy=3`}
                title="Video Player"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </div>
          </div>
          
          {/* Bottom tap-to-close hint */}
          <p 
            onClick={closeVideoLightbox}
            style={{ 
              position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
              fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.82rem', 
              color: 'rgba(255,255,255,0.6)', cursor: 'pointer', zIndex: 10001,
              padding: '10px 24px', borderRadius: '9999px',
              background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)'
            }}
          >
            Tap here or &times; to close
          </p>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
